
<template>
  <div class="file-uploader">
    <div
        class="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:bg-gray-50 transition-colors cursor-pointer"
        @click="triggerFileInput"
        @drop.prevent="handleDrop"
        @dragover.prevent
        @dragenter.prevent="isDragging = true"
        @dragleave.prevent="isDragging = false"
        :class="{ 'bg-blue-50 border-blue-300': isDragging }"
    >
      <input
        type="file"
        ref="fileInput"
        class="hidden"
        multiple
        @change="handleFileChange"
      />
      <div v-if="uploading" class="flex justify-center items-center gap-2 text-blue-600">
         <i class="pi pi-spin pi-spinner text-xl"></i>
         <span>Uploading...</span>
      </div>
      <div v-else>
         <i class="pi pi-cloud-upload text-3xl text-gray-400 mb-2"></i>
         <p class="text-sm text-gray-600 font-medium">Click to upload or drag and drop</p>
         <p class="text-xs text-gray-500 mt-1">Supports: Images, PDF, Docs</p>
      </div>
    </div>

    <!-- File List -->
    <div v-if="uploadedFiles.length > 0" class="mt-4 space-y-2">
      <div v-for="(file, index) in uploadedFiles" :key="index" class="flex items-center justify-between p-2 bg-gray-50 rounded text-sm">
        <div class="flex items-center gap-2 overflow-hidden">
             <i class="pi pi-file text-gray-500"></i>
             <span class="truncate">{{ file.name }}</span>
        </div>
        <button @click="removeFile(index)" class="text-red-500 hover:text-red-700">
             <i class="pi pi-times"></i>
        </button>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { useFileUpload } from '@/composables/common/useFileUpload';
import { useToast } from 'primevue/usetoast';

const emit = defineEmits(['uploaded']);
const props = defineProps<{
    maxFiles?: number
}>();

const fileInput = ref<HTMLInputElement | null>(null);
const isDragging = ref(false);
const uploadedFiles = ref<{ name: string, id: string }[]>([]);
const { uploadFiles, uploading } = useFileUpload();
const toast = useToast();

const triggerFileInput = () => {
  fileInput.value?.click();
};

const handleFileChange = async(event: Event) => {
  const target = event.target as HTMLInputElement;
  if (target.files) {
    await processFiles(Array.from(target.files));
  }
  // Reset input
  if (fileInput.value) fileInput.value.value = '';
};

const handleDrop = async(event: DragEvent) => {
  isDragging.value = false;
  if (event.dataTransfer?.files) {
    await processFiles(Array.from(event.dataTransfer.files));
  }
};

const processFiles = async(files: File[]) => {
  if (props.maxFiles && (uploadedFiles.value.length + files.length) > props.maxFiles) {
    toast.add({ severity: 'warn', summary: 'Limit Exceeded', detail: `You can only upload up to ${props.maxFiles} files.` });
    return;
  }

  try {
    const ids = await uploadFiles(files);
    // We match IDs back to file names for display (simple assuming order is preserved for now,
    // ideally uploadFiles returns [{id, name}])
    // Refactoring uploadFiles to return objects would be better, but assuming sync order roughly:

    // Actually uploadFiles in composable returned just IDs.
    // Let's improve the UI feedback by trusting the upload succeeded.
    // We will store the name and the returned ID.
    // Ideally we should get the metadata back from the mutation result in the composable.

    // For now, let's assume success and map length
    if (ids && ids.length === files.length) {
      const newFiles = files.map((f, i) => ({
        name: f.name,
        id: ids[i],
      }));
      uploadedFiles.value = [...uploadedFiles.value, ...newFiles];
      emit('uploaded', uploadedFiles.value.map(f => f.id));
    }

  } catch (e) {
    // Error already handled in composable toast
  }
};

const removeFile = (index: number) => {
  uploadedFiles.value.splice(index, 1);
  emit('uploaded', uploadedFiles.value.map(f => f.id));
};
</script>
